import React from 'react';
import { Helmet } from 'react-helmet-async';
import seoConfig from '../config/seoConfig';

/**
 * SEOHelmet Component
 *
 * Usage:
 *   import SEOHelmet from '../components/SEOHelmet';
 *   <SEOHelmet page="home" />
 *
 * For dynamic pages (blog, industry):
 *   <SEOHelmet page="blogDetail" replacements={{ blogTitle: 'My Article', blogExcerpt: 'About ...' }} />
 *   <SEOHelmet page="industryInfo" replacements={{ industry: 'Healthcare' }} />
 */

const SITE_URL = 'https://www.assetflows.in';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.svg`;

function interpolate(template, replacements = {}) {
  if (!template) return template;
  return Object.keys(replacements).reduce(
    (str, key) => str.replace(new RegExp(`\\{${key}\\}`, 'g'), replacements[key]),
    template
  );
}

const SEOHelmet = ({
  page,
  replacements = {},
  title: titleProp,
  description: descProp,
  keywords: keywordsProp,
  ogTitle: ogTitleProp,
  ogDescription: ogDescProp,
  ogImage,
  canonicalUrl,
  noIndex = false,
}) => {
  const config = page && seoConfig[page] ? seoConfig[page] : {};
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const resolvedCanonicalUrl = canonicalUrl || `${SITE_URL}${currentPath === '/' ? '' : currentPath}`;

  const title = titleProp || interpolate(config.title, replacements) || 'AssetFlows – Smart Asset Management Software';
  const description = descProp || interpolate(config.description, replacements) || 'AssetFlows is the all-in-one asset management platform for businesses of all sizes.';
  const keywords = keywordsProp || interpolate(config.keywords, replacements) || 'asset management software, asset tracking';
  const ogTitle = ogTitleProp || interpolate(config.ogTitle, replacements) || title;
  const ogDescription = ogDescProp || interpolate(config.ogDescription, replacements) || description;
  const ogImageUrl = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="AssetFlows Technologies Pvt. Ltd." />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={resolvedCanonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="AssetFlows" />
      <meta property="og:url" content={resolvedCanonicalUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:site" content="@AssetFlows" />

      <meta name="theme-color" content="#4f46e5" />
      <meta name="application-name" content="AssetFlows" />
      <meta name="generator" content="AssetFlows" />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'AssetFlows',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description,
          url: SITE_URL,
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR',
            description: 'Free trial available',
          },
          publisher: {
            '@type': 'Organization',
            name: 'AssetFlows Technologies Pvt. Ltd.',
            url: SITE_URL,
            logo: {
              '@type': 'ImageObject',
              url: `${SITE_URL}/logo192.png`,
            },
          },
        })}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;
